import type { CropData, DriveUploadResult, ProductCategory } from '@/types';
import { logger } from '@/lib/logger';

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';

export class DriveUploadService {
  async uploadCrops(
    crops: CropData[],
    accessToken: string,
    productTitle: string,
    fetchReadmePdf?: (category: ProductCategory) => Promise<Blob | null>,
    productCategory?: ProductCategory
  ): Promise<DriveUploadResult> {
    const mainFolderId = await this.createFolder(productTitle, accessToken);
    const fileIds = await this.uploadCropFiles(crops, mainFolderId, productTitle, accessToken);

    if (productCategory && fetchReadmePdf) {
      await this.uploadReadmePdf(mainFolderId, productCategory, accessToken, fetchReadmePdf);
    }

    await this.setPublicPermissions(mainFolderId, accessToken);
    const folderLink = `https://drive.google.com/drive/folders/${mainFolderId}`;

    return { folderLink, folderId: mainFolderId, fileIds };
  }

  private async createFolder(name: string, accessToken: string, parentId?: string): Promise<string> {
    const body: Record<string, unknown> = {
      name,
      mimeType: 'application/vnd.google-apps.folder',
    };
    if (parentId) body.parents = [parentId];

    const response = await fetch(`${DRIVE_API}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to create folder: ${name}`);
    }

    const folder = await response.json();
    return folder.id;
  }

  private async uploadCropFiles(
    crops: CropData[],
    parentFolderId: string,
    productTitle: string,
    accessToken: string
  ): Promise<Record<string, string>> {
    const fileIds: Record<string, string> = {};

    for (const crop of crops) {
      if (!crop.blob) {
        throw new Error(`Missing blob for ${crop.ratio}`);
      }

      const subfolderId = await this.createFolder(crop.ratio, accessToken, parentFolderId);
      const fileName = `${productTitle}_${crop.ratio}.jpg`;
      
      const metadata = { name: fileName, parents: [subfolderId] };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', crop.blob, fileName);

      const response = await fetch(`${UPLOAD_API}/files?uploadType=multipart`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${fileName} to Google Drive`);
      }

      const file = await response.json();
      fileIds[crop.ratio] = file.id;
    }

    return fileIds;
  }

  private async uploadReadmePdf(
    folderId: string,
    productCategory: ProductCategory,
    accessToken: string,
    fetchReadmePdf: (category: ProductCategory) => Promise<Blob | null>
  ): Promise<void> {
    logger.debug('DriveUpload', `Uploading README PDF for category: ${productCategory}`);
    
    try {
      const pdfBlob = await fetchReadmePdf(productCategory);
      if (!pdfBlob) return;

      const metadata = { name: 'README.pdf', parents: [folderId] };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', pdfBlob, 'README.pdf');

      const response = await fetch(`${UPLOAD_API}/files?uploadType=multipart`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('DriveUpload', 'README upload failed', response.status, errorText);
        throw new Error(`Failed to upload README.pdf: ${response.status}`);
      }

      logger.debug('DriveUpload', 'README PDF uploaded successfully');
    } catch (error) {
      logger.error('DriveUpload', 'Failed to upload README PDF', error);
    }
  }

  private async setPublicPermissions(folderId: string, accessToken: string): Promise<void> {
    await fetch(`${DRIVE_API}/files/${folderId}/permissions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role: 'reader', type: 'anyone' }),
    });
  }
}
